/* LT-ProvSums-01 — Provisional Sums Library edit page.
 * Simple form: description + default_value + display_order + active toggle.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Archive } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Card, PageHeader, PrimaryButton, QuietButton, GhostButton, Field } from "../components/UI";

export default function ProvsumLibraryEdit() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const [form, setForm] = useState({
    description: "", default_value: 0, display_order: "", is_active: true,
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    api.get(`/provsum-library/${id}`)
      .then((r) => setForm({
        description: r.data.description || "",
        default_value: r.data.default_value || 0,
        display_order: r.data.display_order || "",
        is_active: !!r.data.is_active,
      }))
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  const save = async () => {
    if (!form.description || !form.description.trim()) {
      toast.error("Description is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        description: form.description.trim(),
        default_value: Number(form.default_value || 0),
      };
      if (form.display_order !== "") payload.display_order = Number(form.display_order);
      if (isNew) {
        const r = await api.post("/provsum-library", payload);
        toast.success("Description created");
        navigate(`/provsum-library/${r.data.id}`);
      } else {
        await api.put(`/provsum-library/${id}`, { ...payload, is_active: form.is_active });
        toast.success("Saved");
      }
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const archive = async () => {
    if (!window.confirm("Archive this description? Existing tenders are unaffected.")) return;
    try {
      await api.delete(`/provsum-library/${id}`);
      toast.success("Archived");
      navigate("/provsum-library");
    } catch {
      toast.error("Failed to archive");
    }
  };

  if (loading) return <div className="app-container py-8 text-ink-4" style={{ fontSize: 12 }}>Loading…</div>;

  return (
    <div className="app-container py-8 space-y-4" data-testid="provsum-library-edit-page">
      <PageHeader
        title={isNew ? "New provisional sum description" : "Edit description"}
        subtitle="Master list entry. Used to seed new tenders."
        actions={
          <GhostButton icon={ArrowLeft} onClick={() => navigate("/provsum-library")} data-testid="provsum-library-edit-back">
            Back
          </GhostButton>
        }
      />
      <Card>
        <div className="p-5 space-y-4">
          <Field label="Description" required>
            <input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full h-9 px-3 rounded-md border border-line bg-surface text-ink focus:outline-none focus:ring-1 focus:ring-accent"
              style={{ fontSize: 12.5 }}
              placeholder="e.g. Underfloor Heating"
              data-testid="provsum-library-edit-description"
            />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Default value (£)" hint="Usually 0 — prov sums are job-specific.">
              <input
                type="number"
                value={form.default_value}
                onChange={(e) => setForm({ ...form, default_value: e.target.value })}
                min={0}
                step={1}
                className="w-full h-9 px-3 rounded-md border border-line bg-surface text-ink tabular-nums focus:outline-none focus:ring-1 focus:ring-accent"
                style={{ fontSize: 12.5 }}
                data-testid="provsum-library-edit-default-value"
              />
            </Field>
            <Field label="Display order" hint="Lower numbers appear first. Increment of 10 recommended.">
              <input
                type="number"
                value={form.display_order}
                onChange={(e) => setForm({ ...form, display_order: e.target.value })}
                step={10}
                className="w-full h-9 px-3 rounded-md border border-line bg-surface text-ink tabular-nums focus:outline-none focus:ring-1 focus:ring-accent"
                style={{ fontSize: 12.5 }}
                placeholder="auto"
                data-testid="provsum-library-edit-display-order"
              />
            </Field>
          </div>
        </div>
      </Card>
      <div className="flex items-center justify-between">
        <div>
          {!isNew && form.is_active && (
            <QuietButton icon={Archive} onClick={archive} data-testid="provsum-library-edit-archive">
              Archive
            </QuietButton>
          )}
        </div>
        <PrimaryButton icon={Save} onClick={save} disabled={saving} data-testid="provsum-library-edit-save">
          {saving ? "Saving…" : "Save"}
        </PrimaryButton>
      </div>
    </div>
  );
}
